import { useCallback, useEffect, useRef, useState } from "react";
import {
  Mic, Send, Sliders, Volume2, Sparkles, Check, X, Info,
  ShoppingBasket, Plus, Minus, Trash2, Clock, RefreshCw, Heart,
  ChevronDown, ChevronUp,
} from "lucide-react";
import {
  tylussConciergeDecide, getVoiceProfile,
  updateVoiceProfile, voiceProfileNL, getTylussState,
  tylussAnticipation, tylussBasketSdui, tylussBasketFulfill,
  tylussGeminiConverse,
} from "../lib/api";

const CHIP_ICON = { RECURRING_BASKET: RefreshCw, REPLENISHMENT: Clock, FAVORITE: Heart, TIME_OF_DAY: Sparkles };

// Créneaux de livraison planifiée (toujours futurs & réalisables).
const timePresets = () => {
  const mk = (off, h) => { const d = new Date(); d.setDate(d.getDate() + off); d.setHours(h, 0, 0, 0); return d; };
  return [
    { label: "Demain 12h", dt: mk(1, 12) },
    { label: "Demain 19h", dt: mk(1, 19) },
    { label: "Après-demain 19h", dt: mk(2, 19) },
  ];
};

const GOLD = "#E8C98A", DARK = "#0B0F16", ORANGE = "#f97316";

const getRecognition = () => {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;
  const rec = new SR();
  rec.lang = "fr-FR"; rec.interimResults = true; rec.maxAlternatives = 1;
  return rec;
};

const summarizeDecide = (r) =>
  r.message || (r.status === "EXECUTED" ? "C'est fait." :
    r.status === "HANDOFF" ? "Demande transmise (aucun paiement effectué)." :
    r.status === "BLOCKED_NO_PROVIDER" ? "Action indisponible : aucun fournisseur branché." :
    r.status === "NO_CHANGE" ? "Très bien, je ne change rien." : r.status);

export default function Tyluss() {
  const [messages, setMessages] = useState([
    { who: "tyluss", text: "Bonjour, je suis Tyluss, votre concierge. Parlez ou écrivez : dites ce que vous voulez, je détermine le comment." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [partial, setPartial] = useState("");
  const [consent, setConsent] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [nlText, setNlText] = useState("");
  const [greeting, setGreeting] = useState("");
  const [chips, setChips] = useState([]);
  const [basketOpen, setBasketOpen] = useState(false);
  const [cards, setCards] = useState([]);          // cartes produit réelles (source de vérité panier)
  const [proposals, setProposals] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState({});
  const typeRef = useRef(null);
  const [subtotal, setSubtotal] = useState(null);
  const [unmatched, setUnmatched] = useState([]);
  const [basketBusy, setBasketBusy] = useState(false);
  const [slot, setSlot] = useState(timePresets()[1]);
  const [addr, setAddr] = useState("");
  const [needAddr, setNeedAddr] = useState(false);
  const recRef = useRef(null), scrollRef = useRef(null);
  const audioCtxRef = useRef(null), analyserRef = useRef(null), streamRef = useRef(null);
  const rafRef = useRef(null), speakingRef = useRef(false);
  const voiceSupported = !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  const ttsSupported = !!window.speechSynthesis;

  const load = useCallback(async () => {
    try {
      const st = await getTylussState();
      if (st.voice_profile) setProfile(st.voice_profile);
      if (Array.isArray(st.conversation) && st.conversation.length) {
        setMessages(st.conversation.map((t) => ({ who: t.role === "client" ? "client" : "tyluss", text: t.text })));
      }
      setConsent(st.pending_action ? { session_id: st.pending_action.session_id, label: st.pending_action.label || "" } : null);
    } catch {
      try { setProfile(await getVoiceProfile()); } catch { /* offline */ }
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Anticipation : salutation + pastilles prêtes à taper (historique, favoris, moment).
  const loadChips = useCallback(async () => {
    try {
      const a = await tylussAnticipation();
      setGreeting(a.greeting || "");
      setChips(Array.isArray(a.chips) ? a.chips : []);
    } catch { /* silencieux : le chat reste utilisable */ }
  }, []);
  useEffect(() => { loadChips(); }, [loadChips]);

  const subtotalOf = (cs) => {
    const vals = cs.filter((c) => typeof c.price === "number").map((c) => c.price * c.quantity);
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) * 100) / 100 : null;
  };

  // Re-résout le panier via le catalogue réel (prix/photos/apparié) — source de vérité serveur.
  const resync = useCallback(async (queries) => {
    if (!queries.length) { setCards([]); setSubtotal(null); setUnmatched([]); return; }
    setBasketBusy(true);
    try {
      const r = await tylussBasketSdui(queries);
      setCards(r.cards || []);
      setSubtotal(r.estimated_subtotal);
      setUnmatched(r.unmatched || []);
    } finally { setBasketBusy(false); }
  }, []);

  const openChip = async (chip) => {
    const existing = cards.map((c) => `${c.quantity} ${c.matched_from}`);
    const add = (chip.items || []).map((it) => `1 ${it.title}`);
    setBasketOpen(true);
    await resync([...existing, ...add]);
  };

  const changeQty = (idx, delta) => {
    setCards((cs) => {
      const next = cs.map((c, i) => i === idx ? { ...c, quantity: Math.max(1, c.quantity + delta) } : c);
      setSubtotal(subtotalOf(next));
      return next;
    });
  };
  const removeCard = (idx) => {
    setCards((cs) => { const next = cs.filter((_, i) => i !== idx); setSubtotal(subtotalOf(next)); if (!next.length) setBasketOpen(true); return next; });
  };
  const addToBasket = async (text) => {
    const t = (text || "").trim(); if (!t) return;
    const queries = [...cards.map((c) => `${c.quantity} ${c.matched_from}`), `1 ${t}`];
    await resync(queries);
  };

  const validateBasket = async () => {
    if (!cards.length) return;
    setBasketBusy(true);
    const items = cards.map((c) => `${c.quantity} ${c.matched_from}`);
    const payload = { items, scheduled_delivery_at: slot.dt.toISOString() };
    if (addr.trim()) payload.address = addr.trim();
    try {
      const r = await tylussBasketFulfill(payload);
      setBasketOpen(false); setCards([]); setSubtotal(null); setNeedAddr(false); setAddr("");
      push("tyluss", `C'est planifié ✅ Commande ${r.order_number} pour ${slot.label}. `
        + `Votre livraison vous attendra à la voiture. Aucun paiement effectué — validation finale à la livraison.`);
      loadChips();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.reason === "ADDRESS_REQUIRED") { setNeedAddr(true); push("tyluss", "Indiquez-moi votre adresse de livraison pour planifier."); }
      else push("tyluss", (d?.message || d) ? `Je n'ai pas pu planifier : ${d.message || d}` : "La planification a échoué, réessayez.");
    } finally { setBasketBusy(false); }
  };
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, loading]);
  useEffect(() => () => { if (typeRef.current) clearInterval(typeRef.current); }, []);

  // VAD + réduction bruit/écho (WebRTC navigateur) → barge-in.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) return;
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        streamRef.current = stream;
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx(); audioCtxRef.current = ctx;
        const analyser = ctx.createAnalyser(); analyser.fftSize = 512; analyserRef.current = analyser;
        ctx.createMediaStreamSource(stream).connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);
        const loop = () => {
          analyser.getByteTimeDomainData(data);
          let sum = 0; for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
          const active = Math.sqrt(sum / data.length) > 0.06;
          if (active && speakingRef.current) { window.speechSynthesis?.cancel(); speakingRef.current = false; } // barge-in
          rafRef.current = requestAnimationFrame(loop);
        };
        loop();
      } catch { /* micro refusé → VAD indispo, fallback texte reste */ }
    })();
    return () => {
      mounted = false; cancelAnimationFrame(rafRef.current);
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); audioCtxRef.current?.close(); } catch { /* */ }
    };
  }, []);

  const directives = (v) => (v ? { lang: "fr-FR", rate: v.rate || 1, pitch: 0.5 + (v.energy ?? 0.5) } : { lang: "fr-FR" });

  // TTS streaming : découpe en phrases, démarre dès la 1re (start-before-full) + mesure 1re voix.
  const speakStreaming = (text, d) => {
    if (!window.speechSynthesis || !text) return;
    window.speechSynthesis.cancel();
    const sentences = text.match(/[^.!?]+[.!?]*/g) || [text];
    speakingRef.current = true;
    sentences.forEach((s, idx) => {
      const u = new SpeechSynthesisUtterance(s.trim());
      u.lang = d?.lang || "fr-FR"; u.rate = d?.rate || 1; u.pitch = d?.pitch || 1;
      if (idx === sentences.length - 1) u.onend = () => { speakingRef.current = false; };
      window.speechSynthesis.speak(u);
    });
  };

  const push = (who, text) => setMessages((m) => [...m, { who, text }]);

  const revealReply = (full) => {
    if (typeRef.current) clearInterval(typeRef.current);
    const text = full || "";
    let i = 0;
    push("tyluss", "");
    typeRef.current = setInterval(() => {
      i += 2;
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { who: "tyluss", text: text.slice(0, i) }; return c; });
      if (i >= text.length) { clearInterval(typeRef.current); typeRef.current = null; }
    }, 18);
  };

  // Mode FLUIDE : Gemini analyse la sémantique (toute la galaxie La BASE) → propositions + message.
  // UX immédiate : message client (0 ms) + squelette, puis accordéon réel + message révélé.
  const sendTurn = async (text, viaVoice = false) => {
    const t = (text || "").trim(); if (!t) return;
    push("client", t); setInput("");
    setProposals(null); setSelected({}); setExpanded({});
    setLoading(true);
    try {
      const r = await tylussGeminiConverse(t);          // Gemini (sémantique) — source unique
      setProposals(r);
      if (r.choices?.length) setExpanded({ [r.choices[0].requested]: true });
      revealReply(r.reply || "Voici mes suggestions, à valider.");
      if (viaVoice) speakStreaming(r.reply, directives(profile));
    } catch {
      push("tyluss", "Je reste disponible ; réessayez dans un instant.");
    } finally { setLoading(false); }
  };

  const addProposalsToBasket = async () => {
    if (!proposals) return;
    const q = [...cards.map((c) => `${c.quantity} ${c.matched_from}`)];
    Object.values(selected).forEach((o) => q.push(`1 ${o.title}`));
    (proposals.items || []).forEach((it) => q.push(`${it.quantity || 1} ${it.title}`));
    (proposals.to_source || []).forEach((s) => q.push(`1 ${s.requested}`));
    setBasketOpen(true);
    await resync(q);
  };

  const decide = async (authorize, viaVoice = false) => {
    if (!consent) return; setLoading(true);
    try {
      const r = await tylussConciergeDecide(consent.session_id, authorize);
      setConsent(null); const txt = summarizeDecide(r); push("tyluss", txt);
      if (viaVoice) speakStreaming(txt, directives(profile));
      await load();
    } catch { push("tyluss", "L'action n'a pas abouti, réessayez."); } finally { setLoading(false); }
  };

  const startListening = () => {
    if (!voiceSupported) return;
    window.speechSynthesis?.cancel(); speakingRef.current = false; // barge-in
    const rec = getRecognition(); if (!rec) return;
    recRef.current = rec; setListening(true); setPartial("");
    rec.onspeechstart = () => { window.speechSynthesis?.cancel(); speakingRef.current = false; };
    rec.onresult = (e) => {
      let interim = "", final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const tr = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += tr; else interim += tr;
      }
      setPartial(interim);
      if (final) { setPartial(""); setListening(false); sendTurn(final, true); }
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    rec.start();
  };
  const stopListening = () => { try { recRef.current?.stop(); } catch { /* */ } setListening(false); };

  const saveField = async (key, value) => { try { setProfile(await updateVoiceProfile({ [key]: value })); } catch { /* */ } };
  const applyNL = async () => {
    const t = nlText.trim(); if (!t) return;
    try { const r = await voiceProfileNL(t); setProfile(r.profile); setNlText(""); push("tyluss", r.message); } catch { /* */ }
  };

  const p = profile;
  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="tyluss-page">
      <div className="rounded-2xl p-5 mb-4 flex items-center justify-between" style={{ background: `linear-gradient(135deg,${DARK},#1E0A03)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: GOLD }}><Sparkles size={22} color={DARK} /></div>
          <div>
            <p className="text-xl font-extrabold tracking-widest" style={{ color: GOLD }}>TYLUSS</p>
            <p className="text-xs" style={{ color: "#8a8f98" }}>Concierge vocal — web</p>
          </div>
        </div>
        <button onClick={() => setShowSettings((v) => !v)} data-testid="tyluss-settings-toggle" className="p-2 rounded-lg" style={{ background: "rgba(232,201,138,0.15)", color: GOLD }} title="Réglages voix"><Sliders size={20} /></button>
      </div>

      <div className="flex items-center gap-2 text-xs mb-3 px-3 py-2 rounded-lg" style={{ background: "#fff7ed", color: "#9a3412", border: "1px solid #fed7aa" }} data-testid="voice-preview-note">
        <Info size={14} />
        <span>Assistant vocal en aperçu. La reconnaissance et la synthèse vocales dépendent de votre navigateur ; le mode écrit reste toujours disponible.</span>
      </div>

      {showSettings ? (
        <VoiceSettings p={p} onSave={saveField} nlText={nlText} setNlText={setNlText} onNL={applyNL} ttsSupported={ttsSupported} onTest={() => speakStreaming("Bonjour, voici ma voix actuelle.", directives(p))} />
      ) : (
        <>
          <div ref={scrollRef} className="rounded-2xl p-4 mb-3 overflow-y-auto bg-white" style={{ height: 400, border: "1px solid #f0f0f0" }} data-testid="tyluss-chat">
            <div className="flex flex-col gap-2.5">
              {messages.map((m, i) => (
                <div key={i} data-testid={`msg-${m.who}`} className="max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm" style={m.who === "client" ? { alignSelf: "flex-end", background: DARK, color: "#fff" } : { alignSelf: "flex-start", background: "#f2f4f7", color: DARK }}>{m.text}</div>
              ))}

              {proposals && (
                <div className="flex flex-col gap-2 mt-1 w-full" data-testid="tyluss-proposals">
                  {proposals.intro && <p className="text-xs" style={{ color: "#475569" }}>{proposals.intro}</p>}
                  {(proposals.choices || []).map((c) => {
                    const open = !!expanded[c.requested];
                    const sel = selected[c.requested];
                    return (
                      <div key={c.requested} className="rounded-xl overflow-hidden bg-white" style={{ border: "1px solid #eee" }} data-testid={`acc-${c.requested}`}>
                        <button onClick={() => setExpanded((e) => ({ ...e, [c.requested]: !open }))} data-testid={`acc-head-${c.requested}`} className="w-full flex items-center justify-between px-3.5 py-3" style={{ background: "#fafafa" }}>
                          <span className="text-sm font-bold" style={{ color: DARK }}>{c.requested}</span>
                          <span className="flex items-center gap-2">
                            {sel ? <span className="text-xs font-semibold truncate max-w-[160px]" style={{ color: "#0a7d2c" }}>{sel.title}</span> : <span className="text-xs" style={{ color: "#8a8f98" }}>{c.options.length} options</span>}
                            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </span>
                        </button>
                        {open && c.options.map((o) => {
                          const isSel = sel?.product_id === o.product_id;
                          return (
                            <button key={o.product_id} onClick={() => setSelected((s) => ({ ...s, [c.requested]: o }))} data-testid={`acc-opt-${o.product_id}`} className="w-full flex items-center gap-2.5 px-3 py-2 text-left" style={{ borderTop: "1px solid #f0f0f0", background: isSel ? "#f0fbf3" : "#fff" }}>
                              <div className="w-10 h-10 rounded-lg bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                                {o.image_url ? <img src={o.image_url} alt={o.title} className="w-full h-full object-cover" /> : <ShoppingBasket size={15} color="#94a3b8" />}
                              </div>
                              <span className="flex-1 text-sm" style={{ color: DARK }}>{o.title}</span>
                              <span className="text-sm font-bold whitespace-nowrap" style={{ color: DARK }}>{typeof o.price === "number" ? `${o.price.toFixed(2)} ${o.currency || "CHF"}` : "—"}</span>
                              {isSel && <Check size={16} color="#0a7d2c" />}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                  {(proposals.to_source || []).length > 0 && (
                    <div className="rounded-xl bg-white px-3.5 py-3" style={{ border: "1px solid #eee" }} data-testid="acc-personal-shopper">
                      <p className="text-sm font-bold" style={{ color: DARK }}>Personal Shopper (planifié)</p>
                      <p className="text-xs mt-1" style={{ color: "#9a3412" }}>{proposals.to_source.map((s) => s.requested).join(", ")} — courses faites avant la fermeture, stockées à La BASE, livrées au créneau.</p>
                    </div>
                  )}
                  <button onClick={addProposalsToBasket} data-testid="proposals-add-basket" className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold" style={{ background: DARK, color: "#fff" }}>
                    <ShoppingBasket size={16} /> Ajouter au panier
                  </button>
                </div>
              )}
              {partial && <div className="max-w-[85%] px-3.5 py-2 rounded-2xl text-sm italic" style={{ alignSelf: "flex-end", background: "#e2e8f0", color: "#475569" }} data-testid="voice-partial">{partial}…</div>}
              {loading && !proposals && (
                <div className="flex flex-col gap-2 mt-1 w-full" data-testid="tyluss-skeleton">
                  {[0, 1, 2].map((k) => (
                    <div key={k} className="rounded-xl bg-white overflow-hidden" style={{ border: "1px solid #eee" }}>
                      <div className="flex items-center justify-between px-3.5 py-3" style={{ background: "#fafafa" }}>
                        <span className="h-3 rounded animate-pulse" style={{ width: 120, background: "#e5e7eb" }} />
                        <span className="h-3 rounded animate-pulse" style={{ width: 48, background: "#eceef1" }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {loading && (
                <div className="flex items-center gap-2 self-start px-3.5 py-2.5 rounded-2xl" style={{ background: "#f2f4f7" }} data-testid="tyluss-thinking">
                  <span className="flex gap-1">
                    <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: GOLD, animationDelay: "0ms" }} />
                    <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: GOLD, animationDelay: "150ms" }} />
                    <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: GOLD, animationDelay: "300ms" }} />
                  </span>
                  <span className="text-xs font-semibold" style={{ color: "#64748b" }}>Tyluss comprend votre demande…</span>
                </div>
              )}
              {consent && (
                <div className="flex gap-2 mt-1" data-testid="tyluss-consent">
                  <button onClick={() => decide(true, ttsSupported)} data-testid="tyluss-authorize-btn" className="flex items-center gap-1 px-4 py-2 rounded-full text-sm font-bold" style={{ background: "#0a7d2c", color: "#fff" }}><Check size={15} /> Autoriser</button>
                  <button onClick={() => decide(false)} data-testid="tyluss-decline-btn" className="flex items-center gap-1 px-4 py-2 rounded-full text-sm font-bold" style={{ background: "#fff", color: DARK, border: "1px solid #e6e6e6" }}><X size={15} /> Refuser</button>
                </div>
              )}
            </div>
          </div>

          {(greeting || chips.length > 0) && (
            <div className="mb-2" data-testid="tyluss-anticipation">
              {greeting && <p className="text-sm font-semibold mb-1.5" style={{ color: "#0b1220" }}>{greeting}</p>}
              <div className="flex gap-2 overflow-x-auto pb-1">
                {chips.map((c) => {
                  const Icon = CHIP_ICON[c.kind] || Sparkles;
                  return (
                    <button key={c.id} onClick={() => openChip(c)} data-testid={`chip-${c.kind}`}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap shrink-0"
                      style={{ background: "#fff7ed", color: "#9a3412", border: "1px solid #fed7aa" }}>
                      <Icon size={14} /> {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <button onClick={listening ? stopListening : startListening} disabled={!voiceSupported || loading} data-testid="tyluss-mic-btn" title={voiceSupported ? "Push-to-talk" : "Micro non supporté"} className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 transition-colors" style={{ background: listening ? ORANGE : (voiceSupported && !loading ? DARK : "#cbd5e1"), color: "#fff" }}><Mic size={20} /></button>
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !loading && sendTurn(input)} disabled={loading} placeholder={listening ? "Je vous écoute…" : "Parlez ou écrivez à Tyluss…"} data-testid="tyluss-input" className="flex-1 rounded-full px-4 py-3 text-sm disabled:opacity-60" style={{ border: "1px solid #e6e6e6" }} />
            <button onClick={() => sendTurn(input)} disabled={loading || !input.trim()} data-testid="tyluss-send-btn" className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 transition-opacity disabled:opacity-50" style={{ background: DARK, color: "#fff" }}><Send size={18} /></button>
            <button onClick={() => { setBasketOpen(true); }} data-testid="tyluss-basket-btn" title="Mon panier" className="relative w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(232,201,138,0.2)", color: DARK }}>
              <ShoppingBasket size={20} />
              {cards.length > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: ORANGE, color: "#fff" }}>{cards.length}</span>}
            </button>
          </div>
          {!voiceSupported && <p className="text-xs mt-2" style={{ color: "#8a8f98" }} data-testid="voice-fallback-note">Voix non disponible sur ce navigateur — le mode texte reste pleinement fonctionnel (fallback).</p>}
        </>
      )}

      {basketOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" style={{ background: "rgba(0,0,0,0.45)" }} data-testid="tyluss-basket">
          <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><ShoppingBasket size={20} color={DARK} /><span className="font-extrabold" style={{ color: DARK }}>Mon panier</span></div>
              <button onClick={() => setBasketOpen(false)} data-testid="basket-close" className="p-1.5 rounded-lg" style={{ background: "#f1f5f9" }}><X size={18} /></button>
            </div>

            {cards.length === 0 ? (
              <p className="text-sm py-6 text-center" style={{ color: "#8a8f98" }}>Votre panier est vide. Tapez une suggestion ou ajoutez un article.</p>
            ) : (
              <div className="flex flex-col gap-2.5">
                {cards.map((c, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-xl" style={{ border: "1px solid #f0f0f0" }} data-testid="basket-card">
                    <div className="w-14 h-14 rounded-lg bg-slate-100 overflow-hidden shrink-0">
                      {c.image_url ? <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ShoppingBasket size={18} color="#94a3b8" /></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: DARK }}>{c.name}</p>
                      <p className="text-xs" style={{ color: c.source === "LABASE_CATALOG" ? "#0a7d2c" : "#9a3412" }}>
                        {typeof c.price === "number" ? `${c.price.toFixed(2)} ${c.currency || "CHF"}` : "À sourcer par le Personal Shopper"}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => changeQty(i, -1)} data-testid="basket-dec" className="w-7 h-7 rounded-full flex items-center justify-center" style={{ border: "1px solid #e6e6e6" }}><Minus size={14} /></button>
                      <span className="text-sm font-bold min-w-[20px] text-center">{c.quantity}</span>
                      <button onClick={() => changeQty(i, 1)} data-testid="basket-inc" className="w-7 h-7 rounded-full flex items-center justify-center" style={{ border: "1px solid #e6e6e6" }}><Plus size={14} /></button>
                      <button onClick={() => removeCard(i)} data-testid="basket-remove" className="w-7 h-7 rounded-full flex items-center justify-center ml-1" style={{ color: "#c0392b" }}><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <BasketAdd onAdd={addToBasket} busy={basketBusy} />

            {subtotal != null && <p className="text-sm font-bold mt-3" style={{ color: DARK }}>Sous-total estimé : {subtotal.toFixed(2)} CHF</p>}
            {unmatched.length > 0 && <p className="text-xs mt-1" style={{ color: "#9a3412" }}>À sourcer (prix confirmé au ramassage) : {unmatched.join(", ")}</p>}

            <p className="text-xs font-bold mt-4 mb-1.5" style={{ color: "#64748b" }}>CRÉNEAU DE LIVRAISON</p>
            <div className="flex gap-2 flex-wrap">
              {timePresets().map((s) => (
                <button key={s.label} onClick={() => setSlot(s)} data-testid={`slot-${s.label}`} className="px-3 py-2 rounded-full text-xs font-semibold" style={slot.label === s.label ? { background: DARK, color: "#fff" } : { background: "#f1f5f9", color: DARK }}><Clock size={12} className="inline mr-1" />{s.label}</button>
              ))}
            </div>

            {needAddr && (
              <input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="Votre adresse de livraison" data-testid="basket-address" className="w-full mt-3 rounded-lg px-3 py-2.5 text-sm" style={{ border: "1px solid #e6e6e6" }} />
            )}

            <button onClick={validateBasket} disabled={basketBusy || cards.length === 0} data-testid="basket-validate" className="w-full mt-4 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: "#0a7d2c", color: "#fff" }}>
              {basketBusy ? "…" : <><Check size={16} /> Valider et planifier ({slot.label})</>}
            </button>
            <p className="text-[11px] text-center mt-2" style={{ color: "#94a3b8" }}>Aucun paiement maintenant — validation finale à la livraison.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function BasketAdd({ onAdd, busy }) {
  const [t, setT] = useState("");
  const submit = () => { if (t.trim()) { onAdd(t); setT(""); } };
  return (
    <div className="flex gap-2 mt-3">
      <input value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} disabled={busy} placeholder="Ajouter un article…" data-testid="basket-add-input" className="flex-1 rounded-lg px-3 py-2 text-sm" style={{ border: "1px solid #e6e6e6" }} />
      <button onClick={submit} disabled={busy || !t.trim()} data-testid="basket-add-btn" className="px-3 py-2 rounded-lg text-sm font-bold disabled:opacity-50" style={{ background: "#E8C98A", color: "#0B0F16" }}><Plus size={16} /></button>
    </div>
  );
}

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between py-2.5" style={{ borderBottom: "1px solid #f1f5f9" }}>
    <span className="text-sm font-semibold" style={{ color: "#0b1220" }}>{label}</span>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

const Seg = ({ field, options, p, onSave }) => (
  <div className="flex gap-1">
    {options.map((o) => (
      <button key={o} onClick={() => onSave(field, o)} data-testid={`voice-${field}-${o}`} className="px-2.5 py-1 rounded-full text-xs font-semibold" style={p[field] === o ? { background: DARK, color: "#fff" } : { background: "#f1f5f9", color: "#0b1220" }}>{o}</button>
    ))}
  </div>
);

const Slider = ({ field, min, max, step, p, onSave }) => {
  const [val, setVal] = useState(p[field]);
  useEffect(() => { setVal(p[field]); }, [p, field]);
  const commit = () => onSave(field, parseFloat(val));
  return (
    <input type="range" min={min} max={max} step={step} value={val} data-testid={`voice-${field}-slider`}
      onChange={(e) => setVal(e.target.value)} onMouseUp={commit} onTouchEnd={commit} onKeyUp={commit} style={{ width: 140 }} />
  );
};

function VoiceSettings({ p, onSave, nlText, setNlText, onNL, onTest, ttsSupported }) {
  if (!p) return <div className="text-sm" style={{ color: "#8a8f98" }}>Chargement du profil…</div>;
  return (
    <div className="bg-white rounded-2xl p-5" style={{ border: "1px solid #f0f0f0" }} data-testid="voice-settings">
      <h2 className="text-sm font-extrabold tracking-widest mb-1" style={{ color: "#64748b" }}>VOIX &amp; PERSONNALITÉ</h2>
      <p className="text-xs mb-3" style={{ color: "#94a3b8" }}>{"Synchronisé sur tous vos appareils. Vos préférences restent les mêmes partout."}</p>
      <Row label={`Débit (${p.rate})`}><Slider field="rate" min={0.5} max={2} step={0.1} p={p} onSave={onSave} /></Row>
      <Row label={`Énergie (${p.energy})`}><Slider field="energy" min={0} max={1} step={0.1} p={p} onSave={onSave} /></Row>
      <Row label={`Humour (${p.humor})`}><Slider field="humor" min={0} max={1} step={0.1} p={p} onSave={onSave} /></Row>
      <Row label={`Proactivité (${p.proactivity})`}><Slider field="proactivity" min={0} max={1} step={0.1} p={p} onSave={onSave} /></Row>
      <Row label="Tempérament"><Seg field="temperament" options={["calm", "neutral", "warm", "dynamic"]} p={p} onSave={onSave} /></Row>
      <Row label="Style"><Seg field="style" options={["direct", "conversational"]} p={p} onSave={onSave} /></Row>
      <Row label="Formalité"><Seg field="formality" options={["informal", "neutral", "formal"]} p={p} onSave={onSave} /></Row>
      <Row label="Détail"><Seg field="detail" options={["concise", "balanced", "detailed"]} p={p} onSave={onSave} /></Row>
      <div className="mt-4">
        <p className="text-xs font-bold mb-1" style={{ color: "#64748b" }}>RÉGLER EN LANGAGE NATUREL</p>
        <div className="flex gap-2">
          <input value={nlText} onChange={(e) => setNlText(e.target.value)} placeholder='Ex : "parle plus vite", "sois plus direct"' data-testid="voice-nl-input" className="flex-1 rounded-lg px-3 py-2 text-sm" style={{ border: "1px solid #e6e6e6" }} />
          <button onClick={onNL} data-testid="voice-nl-apply" className="px-3 py-2 rounded-lg text-sm font-bold" style={{ background: GOLD, color: DARK }}>Appliquer</button>
        </div>
      </div>
      {ttsSupported && <button onClick={onTest} data-testid="voice-test-btn" className="mt-4 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold" style={{ background: DARK, color: "#fff" }}><Volume2 size={16} /> Tester la voix</button>}
    </div>
  );
}
